// src/modules/notas/CalculoNotas.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { db, db_notas, paths } from '../../firebase/config';
import { doc, setDoc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { Button } from '../../components/Button';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Modal } from '../../components/Modal';
import './CalculoNotas.css';

// --- Ícones de Interface ---
const EditIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
);
const BlockIcon = () => (
  <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
);

// --- Ícones das Abas ---
const CalculatorIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
);
const ChartBarIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
);

// --- Ícones dos Cards (SVG Nativos) ---
const IntellectualIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
  </svg>
);

const ConductIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
  </svg>
);

const FinalIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 3L1 9l11 6 9-4.91V17h2V9M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82z"/>
  </svg>
);

// --- CONSTANTES ---
const MESES = [
  "janeiro", "fevereiro", "marco", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"
];
const CONDUTA_INICIAL = MESES.reduce((acc, mes) => ({ ...acc, [mes]: null }), {});
const MATERIA_INICIAL = { nome: '', vc1: null, vc2: null, ve: null };

// --- Conteúdo HTML da Classificação (mantido igual) ---
const getClassificacaoHtml = (googleSheetUrl, pageTitle) => `
<!DOCTYPE html>
<html lang="pt-br">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${pageTitle}</title>
    <script src="https://cdn.tailwindcss.com"></script>
    <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        body { font-family: 'Inter', sans-serif; background-color: #ffffff; }
        ::-webkit-scrollbar { width: 8px; height: 8px; }
        ::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        ::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spinner { border: 4px solid rgba(0, 0, 0, 0.1); border-left-color: #3b82f6; border-radius: 50%; width: 40px; height: 40px; animation: spin 1s linear infinite; }
        .tab-button { padding: 1rem 0.5rem; border-bottom: 3px solid transparent; font-size: 0.95rem; font-weight: 600; color: #4b5563; transition: all 0.2s ease-in-out; cursor: pointer; text-align: center; }
        .tab-button:hover { color: #1d4ed8; border-bottom-color: #d1d5db; }
        .tab-button.active { color: #2563eb; border-bottom-color: #2563eb; }
        .tab-content { display: none; }
        .tab-content.active { display: block; animation: fadeIn 0.3s ease-out; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
    </style>
</head>
<body class="bg-white p-0"> 
    <div class="mx-auto overflow-hidden">
        <div class="p-4 sm:p-6 flex flex-col-reverse sm:flex-row justify-between items-center sm:items-start">
            <div class="ml-0 sm:ml-6 flex-shrink-0">
                <img src="brasao.png" alt="Brasão" class="h-20 w-20 object-contain mx-auto" onerror="this.style.display='none';">
            </div>
        </div>
        <div class="px-4 sm:px-6 border-b border-gray-200 flex flex-col sm:flex-row justify-between sm:items-end gap-4">
            <nav class="flex -mb-px space-x-6" aria-label="Tabs">
                <button data-tab="classificacao" class="tab-button active">Classificação Geral</button>
                <button data-tab="calculadora" class="tab-button">Calculadora Pessoal</button>
            </nav>
            <div class="flex flex-col sm:flex-row gap-4 sm:items-end pb-1">
                <div class="flex-1 min-w-0">
                    <label for="search-box" class="block text-xs font-medium text-gray-700">Pesquisar por nome:</label>
                    <input type="text" id="search-box" placeholder="Digite o nome..." class="w-full p-1 border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-00">
                </div>
                <div class="flex-shrink-0">
                    <label for="filter-select" class="block text-xs font-medium text-gray-700">Ordenar por:</label>
                    <select id="filter-select" class="block w-full sm:w-auto p-1 border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-300">
                        <option value="mediaGeral">Média Geral (Provisória)</option>
                        <option value="cfo1">Nota 1º CFO</option>
                        <option value="cfo2">Nota 2º CFO</option>
                        <option value="cfo3">Nota 3º CFO</option>
                        <option value="cfo4">Nota 4º CFO</option>
                    </select>
                </div>
            </div>
        </div>
        <div id="tab-content-classificacao" class="tab-content active">
            <div class="overflow-x-auto mx-4 sm:mx-6 pb-4 sm:pb-6 pt-4">
                <div class="overflow-hidden rounded-lg border border-gray-200">
                    <table class="w-full min-w-[800px] border-collapse">
                        <thead class="bg-gray-200 border-b-2 border-gray-300">
                            <tr>
                                <th rowspan="2" class="p-2 text-left text-sm font-semibold text-gray-700 uppercase">Nome do Cadete</th>
                                <th colspan="2" class="p-2 text-center text-sm font-semibold text-gray-700 uppercase border-l border-gray-300">1º CFO</th>
                                <th colspan="2" class="p-2 text-center text-sm font-semibold text-gray-700 uppercase border-l border-gray-300">2º CFO</th>
                                <th colspan="2" class="p-2 text-center text-sm font-semibold text-gray-700 uppercase border-l border-gray-300">3º CFO</th>
                                <th colspan="2" class="p-2 text-center text-sm font-semibold text-gray-700 uppercase border-l border-gray-300">4º CFO</th>
                                <th colspan="2" id="header-resultado-final" class="p-2 text-center text-sm font-semibold text-gray-700 uppercase bg-gray-300 border-l border-gray-400">Resultado (Média Geral)</th>
                            </tr>
                            <tr>
                                <th class="p-2 text-center text-xs font-medium text-gray-600 border-l border-gray-300">Class.</th><th class="p-2 text-center text-xs font-medium text-gray-600">Nota</th>
                                <th class="p-2 text-center text-xs font-medium text-gray-600 border-l border-gray-300">Class.</th><th class="p-2 text-center text-xs font-medium text-gray-600">Nota</th>
                                <th class="p-2 text-center text-xs font-medium text-gray-600 border-l border-gray-300">Class.</th><th class="p-2 text-center text-xs font-medium text-gray-600">Nota</th>
                                <th class="p-2 text-center text-xs font-medium text-gray-600 border-l border-gray-300">Class.</th><th class="p-2 text-center text-xs font-medium text-gray-600">Nota</th>
                                <th id="header-class-final" class="p-2 text-center text-xs font-medium text-gray-600 bg-gray-300 border-l border-gray-400">Class. Geral</th>
                                <th id="header-nota-final" class="p-2 text-center text-xs font-medium text-gray-600 bg-gray-300">Média Geral</th>
                            </tr>
                        </thead>
                        <tbody id="classification-body" class="divide-y divide-gray-200">
                            <tr><td colspan="11" class="p-10"><div class="flex flex-col items-center justify-center text-gray-500"><div class="spinner"></div><span class="mt-3">Carregando classificação...</span></div></td></tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        <div id="tab-content-calculadora" class="tab-content">
            <div class="p-6 sm:p-8">
                <h2 class="text-2xl font-semibold text-gray-800">Calculadora de Média Final</h2>
                <form id="calculator-form" class="mt-6 space-y-4 max-w-lg">
                    <div><label for="calc-nome" class="block text-sm font-medium text-gray-700">Nome do Cadete</label><select id="calc-nome" class="mt-1 block w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500" required><option value="">Carregando nomes...</option></select></div>
                    <div><label for="calc-nota-4cfo" class="block text-sm font-medium text-gray-700">Nota Estimada do 4º CFO (0 a 10)</label><input type="number" id="calc-nota-4cfo" min="0" max="10" step="0.001" class="mt-1 block w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500" required></div>
                    <button type="submit" class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition">Calcular Média</button>
                </form>
                <div id="calculator-result" class="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200" style="display: none;"></div>
            </div>
        </div>
        <div class="p-6 bg-gray-50 border-t border-gray-200">
            <h4 class="text-sm font-semibold text-gray-700">Observações:</h4>
            <ul class="mt-2 list-disc list-inside text-sm text-gray-600 space-y-1">
                <li>Estamos em tratativas com a seção de avaliação para atualizar inconsistências.</li>
                <li>A Média Geral e a Classificação Geral são provisórias.</li>
            </ul>
        </div>
    </div>
    <script>
        // URL INJETADA DINAMICAMENTE
        const googleSheetUrl = '${googleSheetUrl}';
        
        let todosOsAlunosProcessados = [];
        let headerResultadoFinal, headerClassFinal, headerNotaFinal;

        const getNestedValue = (obj, path) => path.split('.').reduce((acc, part) => acc && acc[part], obj);

        function setupTabs() {
            const tabButtons = document.querySelectorAll('.tab-button');
            const tabContents = document.querySelectorAll('.tab-content');
            tabButtons.forEach(button => {
                button.addEventListener('click', () => {
                    tabButtons.forEach(btn => btn.classList.remove('active'));
                    tabContents.forEach(content => content.classList.remove('active'));
                    button.classList.add('active');
                    document.getElementById(\`tab-content-\${button.dataset.tab}\`).classList.add('active');
                });
            });
        }

        function parseCSV(csvText) {
            const data = [];
            const lines = csvText.split(/\\r?\\n/);
            for (let i = 1; i < lines.length; i++) {
                if (!lines[i]) continue;
                const cells = lines[i].split(',');
                if (cells.length < 2) continue;
                const parseCell = (cell) => {
                    if (!cell || cell.trim() === '') return null;
                    const clean = cell.trim().replace(/^"|"$/g, '');
                    return isNaN(parseFloat(clean)) ? clean : parseFloat(clean);
                }
                const aluno = { nome: parseCell(cells[1]), cfo1: { class: parseCell(cells[2]), nota: parseCell(cells[3]) }, cfo2: { class: parseCell(cells[4]), nota: parseCell(cells[5]) }, cfo3: { class: parseCell(cells[6]), nota: parseCell(cells[7]) }, cfo4: { class: parseCell(cells[8]), nota: parseCell(cells[9]) } };
                if(aluno.nome) data.push(aluno);
            }
            return data;
        }

        function tratarNota(valor) {
            if (valor === null || valor === '' || isNaN(valor)) return null;
            let num = Number(valor);
            if (num > 20) {
                return num / 1000;
            }
            return num;
        }

        function processarClassificacoes(data) {
            const comMedias = data.map(aluno => {
                const n1 = tratarNota(aluno.cfo1.nota);
                const n2 = tratarNota(aluno.cfo2.nota);
                const n3 = tratarNota(aluno.cfo3.nota);
                const n4 = tratarNota(aluno.cfo4.nota);
                
                const notas = [n1, n2, n3, n4].filter(n => n !== null);
                const mediaGeral = notas.length > 0 ? notas.reduce((a, b) => a + b, 0) / notas.length : null;
                return { ...aluno, mediaGeral };
            });
            comMedias.sort((a, b) => (b.mediaGeral || 0) - (a.mediaGeral || 0));
            return comMedias.map((aluno, index) => ({ ...aluno, classGeral: aluno.mediaGeral !== null ? index + 1 : null }));
        }

        function renderizarTabela(dados, criterio) {
            const tbody = document.getElementById('classification-body');
            tbody.innerHTML = '';
            const titulos = { mediaGeral: { res: 'Resultado (Média Geral)', class: 'Class. Geral', nota: 'Média Geral' }, cfo1: { res: 'Resultado (1º CFO)', class: 'Class. 1º CFO', nota: 'Nota 1º CFO' }, cfo2: { res: 'Resultado (2º CFO)', class: 'Class. 2º CFO', nota: 'Nota 2º CFO' }, cfo3: { res: 'Resultado (3º CFO)', class: 'Class. 3º CFO', nota: 'Nota 3º CFO' }, cfo4: { res: 'Resultado (4º CFO)', class: 'Class. 4º CFO', nota: 'Nota 4º CFO' } };
            const titulo = titulos[criterio] || titulos.mediaGeral;
            headerResultadoFinal.textContent = titulo.res; headerClassFinal.textContent = titulo.class; headerNotaFinal.textContent = titulo.nota;

            if (dados.length === 0) { tbody.innerHTML = '<tr><td colspan="11" class="p-6 text-center text-gray-500 italic">Nenhum cadete encontrado.</td></tr>'; return; }

            let chaveNota = criterio === 'mediaGeral' ? 'mediaGeral' : \`\${criterio}.nota\`;
            
            dados.forEach((aluno, index) => {
                const tr = document.createElement('tr'); tr.className = "hover:bg-gray-100 transition-colors duration-150";
                
                const fmtCel = (v) => {
                    const val = tratarNota(v);
                    return \`<td class="p-2 text-center text-sm text-gray-700">\${val !== null ? val.toFixed(3) : 'Pendente'}</td>\`;
                };
                
                const fmtClass = (v) => \`<td class="p-2 text-center text-sm font-semibold text-gray-800">\${v ? v+'º' : 'Pendente'}</td>\`;
                
                let valorParaOrdenacao = getNestedValue(aluno, chaveNota);
                if (criterio !== 'mediaGeral') {
                    valorParaOrdenacao = tratarNota(valorParaOrdenacao);
                }
                
                const classFinal = criterio === 'mediaGeral' ? aluno.classGeral : (valorParaOrdenacao !== null ? index + 1 : null);
                
                tr.innerHTML = \`<td class="p-2 pl-4 text-sm font-medium text-gray-900">\${aluno.nome}</td>\` + 
                    fmtClass(aluno.cfo1.class) + fmtCel(aluno.cfo1.nota) + 
                    fmtClass(aluno.cfo2.class) + fmtCel(aluno.cfo2.nota) + 
                    fmtClass(aluno.cfo3.class) + fmtCel(aluno.cfo3.nota) + 
                    fmtClass(aluno.cfo4.class) + fmtCel(aluno.cfo4.nota) + 
                    \`<td class="p-2 text-center text-sm font-bold bg-gray-100 text-blue-800">\${classFinal ? classFinal+'º' : 'Pendente'}</td>\` +
                    \`<td class="p-2 text-center text-sm font-bold bg-gray-100 text-blue-700">\${valorParaOrdenacao !== null ? valorParaOrdenacao.toFixed(3) : 'Pendente'}</td>\`;
                
                tbody.appendChild(tr);
            });
        }

        function ordenarEFiltrarTabela() {
            const term = document.getElementById('search-box').value.toLowerCase();
            const crit = document.getElementById('filter-select').value;
            let lista = [...todosOsAlunosProcessados];
            
            let chave = crit === 'mediaGeral' ? 'mediaGeral' : \`\${crit}.nota\`;
            
            lista.sort((a, b) => {
                let valA = getNestedValue(a, chave);
                let valB = getNestedValue(b, chave);
                
                if (crit !== 'mediaGeral') {
                    valA = tratarNota(valA);
                    valB = tratarNota(valB);
                }
                
                return (valB || -1) - (valA || -1);
            });
            
            if (term) lista = lista.filter(a => a.nome && a.nome.toLowerCase().includes(term));
            renderizarTabela(lista, crit);
        }

        function popularCalculadora(alunos) {
            const sel = document.getElementById('calc-nome');
            sel.innerHTML = '<option value="" disabled selected>Selecione seu nome...</option>';
            alunos.sort((a, b) => a.nome.localeCompare(b.nome)).forEach(a => { if(a.nome) sel.innerHTML += \`<option value="\${a.nome}">\${a.nome}</option>\`; });
        }

        async function init() {
            headerResultadoFinal = document.getElementById('header-resultado-final');
            headerClassFinal = document.getElementById('header-class-final');
            headerNotaFinal = document.getElementById('header-nota-final');
            try {
                const res = await fetch(googleSheetUrl);
                const text = await res.text();
                todosOsAlunosProcessados = processarClassificacoes(parseCSV(text));
                document.getElementById('search-box').addEventListener('input', ordenarEFiltrarTabela);
                document.getElementById('filter-select').addEventListener('change', ordenarEFiltrarTabela);
                ordenarEFiltrarTabela();
                popularCalculadora(todosOsAlunosProcessados);
            } catch (e) { console.error(e); }
        }
        document.addEventListener('DOMContentLoaded', () => { setupTabs(); init(); });
        
        document.getElementById('calculator-form').addEventListener('submit', (e) => {
            e.preventDefault();
            const nome = document.getElementById('calc-nome').value;
            const notaInput = parseFloat(document.getElementById('calc-nota-4cfo').value); 
            
            const aluno = todosOsAlunosProcessados.find(a => a.nome === nome);
            
            const n1 = tratarNota(aluno.cfo1.nota);
            const n2 = tratarNota(aluno.cfo2.nota);
            const n3 = tratarNota(aluno.cfo3.nota);
            
            const notas = [n1, n2, n3, notaInput].filter(n => n!=null);
            const novaMedia = notas.reduce((a,b)=>a+b,0)/4;
            
            let novaClass = 1;
            todosOsAlunosProcessados.forEach(a => { if(a.nome !== nome && (a.mediaGeral||0) > novaMedia) novaClass++; });
            
            document.getElementById('calculator-result').style.display = 'block';
            document.getElementById('calculator-result').innerHTML = \`<h3 class="font-bold">Resultado Simulado</h3><p>Média Final: <strong>\${novaMedia.toFixed(3)}</strong></p><p>Classificação Estimada: <strong>\${novaClass}º</strong></p>\`;
        });
    </script>
</body>
</html>
`;

// --- Componente MediaCard (Layout Flexbox Robusto) ---
const MediaCard = ({ title, value, icon, variant = 'default' }) => {
  const styles = {
    default: {
      cardBg: 'bg-blue-100',
      iconBg: 'bg-white',
      iconColor: 'text-blue-600',
      titleColor: 'text-blue-900',
    },
    conduta: {
      cardBg: 'bg-orange-100',
      iconBg: 'bg-orange-50',
      iconColor: 'text-orange-500',
      titleColor: 'text-orange-900',
    },
    final: {
      cardBg: 'bg-green-100',
      iconBg: 'bg-green-50',
      iconColor: 'text-green-600',
      titleColor: 'text-green-900',
    },
  };

  const currentStyle = styles[variant] || styles.default;

  const renderedIcon = React.isValidElement(icon)
    ? React.cloneElement(icon, {
        className: `w-full h-full ${currentStyle.iconColor}`,
        style: { width: '100%', height: '100%' },
        preserveAspectRatio: 'xMidYMid meet',
      })
    : icon;

  return (
    <div className={`${currentStyle.cardBg} p-3 rounded-lg shadow-sm transform transition-all hover:scale-105 flex items-center gap-3`}>
      <div className={`flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full ${currentStyle.iconBg} shadow-sm`}>
        <div className="w-6 h-6 flex items-center justify-center">
             {renderedIcon}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <h4 className={`text-[10px] font-bold uppercase tracking-wider ${currentStyle.titleColor} opacity-80 truncate`}>
            {title}
        </h4>
        <p className="text-2xl font-extrabold text-gray-900 leading-tight">
          {value.toFixed(3)}
        </p>
      </div>
    </div>
  );
}

// --- Componente Principal ---
const CalculoNotas = () => {
  const { userProfile } = useAuth();
  
  const [globalMaterias, setGlobalMaterias] = useState([]);
  const [userNotas, setUserNotas] = useState([]);
  
  const [turmasList, setTurmasList] = useState([]);
  const [selectedTurma, setSelectedTurma] = useState(null);

  const [conduta, setConduta] = useState(CONDUTA_INICIAL);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [docRef, setDocRef] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false); 
  const [currentMateria, setCurrentItemMateria] = useState(null); 
  const [formData, setFormData] = useState(MATERIA_INICIAL);
  const [isCondutaModalOpen, setIsCondutaModalOpen] = useState(false);
  const [condutaFormData, setCondutaFormData] = useState(CONDUTA_INICIAL);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  
  const [activeTab, setActiveTab] = useState('calculo');

  useEffect(() => {
    if (userProfile?.id) {
      setDocRef(doc(db_notas, "notas_cadetes", userProfile.id));
    }
  }, [userProfile?.id]);

  useEffect(() => {
    const fetchTurmas = async () => {
        try {
            const q = query(collection(db, paths.turmas));
            const snap = await getDocs(q);
            const list = snap.docs.map(d => ({id: d.id, ...d.data()}))
                .filter(t => t.nome && t.nome.startsWith('Aspirantes') && /\d{4}/.test(t.nome));
            
            list.sort((a,b) => a.nome.localeCompare(b.nome));
            setTurmasList(list);
            
            const defaultTurma = list.find(t => t.nome === 'Aspirantes 2026') || list[0];
            setSelectedTurma(defaultTurma);
        } catch (err) {
            console.error("Erro ao buscar turmas:", err);
        }
    };
    fetchTurmas();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const cursosSnap = await getDocs(collection(db, paths.cursos));
        const cursos = cursosSnap.docs.map(d => ({id: d.id, ...d.data()}));
        
        const myCourse = cursos.find(c => 
            (userProfile.turma_nome && userProfile.turma_nome.includes(c.nome)) ||
            (userProfile.pelotao_nome && userProfile.pelotao_nome.includes(c.nome)) ||
            (c.nome && (userProfile.turma_nome === c.nome || userProfile.pelotao_nome === c.nome))
        );

        let subjects = [];
        if (myCourse) {
            const q = query(collection(db, paths.materias), where("curso_id", "==", myCourse.id));
            const matSnap = await getDocs(q);
            // CORREÇÃO: Filtra eventos para não aparecerem nas notas
            subjects = matSnap.docs
                .map(d => ({id: d.id, ...d.data()}))
                .filter(m => m.tipo !== 'evento');
        } else {
            subjects = []; 
        }
        
        subjects.sort((a,b) => a.nome.localeCompare(b.nome));
        setGlobalMaterias(subjects);

        if (docRef) {
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const dados = docSnap.data();
                setUserNotas(dados.materias || []);
                setConduta(dados.conduta || CONDUTA_INICIAL);
            } else {
                setUserNotas([]);
                setConduta(CONDUTA_INICIAL);
            }
        }

      } catch (err) {
        console.error("Erro ao carregar dados:", err);
        setError("Falha ao carregar dados. Tente recarregar a página.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [docRef, userProfile]);

  const materiasExibicao = useMemo(() => {
      return globalMaterias.map(materiaGlobal => {
          const notaSalva = userNotas.find(un => un.id === materiaGlobal.id);
          return {
              ...materiaGlobal,
              vc1: notaSalva?.vc1 ?? null,
              vc2: notaSalva?.vc2 ?? null,
              ve: notaSalva?.ve ?? null
          };
      });
  }, [globalMaterias, userNotas]);

  const handleSave = useCallback(async (novasNotasUsuario, novaConduta) => {
    await setDoc(docRef, { materias: novasNotasUsuario, conduta: novaConduta });
  }, [docRef]);

  const handleOpenModal = (materia) => { 
      setCurrentItemMateria(materia); 
      setFormData(materia ? { 
          nome: materia.nome, 
          vc1: materia.vc1, 
          vc2: materia.vc2, 
          ve: materia.ve 
      } : MATERIA_INICIAL); 
      setError(""); 
      setIsModalOpen(true); 
  };
  
  const handleCloseModal = () => { setIsModalOpen(false); setCurrentItemMateria(null); setFormData(MATERIA_INICIAL); setError(""); };
  
  const handleFormChange = (e) => { 
      const { name, value } = e.target; 
      if (name === 'nome') return; 
      setFormData(prev => ({ ...prev, [name]: (value === "" ? null : parseFloat(value)) })); 
  };

  const handleModalSubmit = async (e) => { 
      e.preventDefault(); 
      setIsProcessing(true); 
      
      const novaNota = {
          id: currentMateria.id,
          vc1: formData.vc1,
          vc2: formData.vc2,
          ve: formData.ve
      };

      const outrasNotas = userNotas.filter(n => n.id !== currentMateria.id);
      const novasNotasUsuario = [...outrasNotas, novaNota];

      try { 
          await handleSave(novasNotasUsuario, conduta); 
          setUserNotas(novasNotasUsuario); 
          handleCloseModal(); 
      } catch (err) { 
          setError(err.message); 
      } finally { 
          setIsProcessing(false); 
      } 
  };

  const handleOpenCondutaModal = () => { setCondutaFormData(conduta); setIsCondutaModalOpen(true); };
  
  const handleCondutaModalSubmit = async (e) => { 
      e.preventDefault(); 
      setIsProcessing(true); 
      await handleSave(userNotas, condutaFormData); 
      setConduta(condutaFormData); 
      setIsCondutaModalOpen(false); 
      setIsProcessing(false); 
  };

  const handleDeleteAllNotes = async () => { 
      setIsProcessing(true); 
      await handleSave([], CONDUTA_INICIAL); 
      setUserNotas([]); 
      setConduta(CONDUTA_INICIAL); 
      setIsDeleteModalOpen(false); 
      setIsProcessing(false); 
  };

  const calcularMediaMateria = (materia) => {
    if (!materia) return 0;
    const vc1 = parseFloat(materia.vc1); const vc2 = parseFloat(materia.vc2); const ve = parseFloat(materia.ve);
    const hasVC1 = !isNaN(vc1); const hasVC2 = !isNaN(vc2); const hasVE = !isNaN(ve);
    
    let media = 0;

    if (hasVC1 && hasVC2) {
        media = (vc1 + vc2) / 2;
    } else if (hasVC1 && hasVE) {
        media = (vc1 * 2 + ve) / 3;
    } else if (hasVC1) {
        media = vc1;
    } else if (hasVC2) {
        media = vc2;
    } else {
        return 0;
    }

    return Math.round(media * 10) / 10;
  };

  const mediaIntelectualGeral = useMemo(() => { 
      const validas = materiasExibicao.map(calcularMediaMateria).filter(m => m > 0); 
      return validas.length ? validas.reduce((a, b) => a + b, 0) / validas.length : 0; 
  }, [materiasExibicao]);

  const mediaConduta = useMemo(() => { const validas = Object.values(conduta).map(n => parseFloat(n)).filter(n => !isNaN(n) && n > 0); return validas.length ? validas.reduce((a, b) => a + b, 0) / validas.length : 0; }, [conduta]);
  const mediaFinal = useMemo(() => { if (mediaIntelectualGeral === 0 && mediaConduta === 0) return 0; return (mediaIntelectualGeral * 2 + mediaConduta) / 3; }, [mediaIntelectualGeral, mediaConduta]);
  
  if (loading) return <LoadingSpinner text="Carregando..." />;

  return (
    <div className="w-full">
        <header className="flex justify-between items-center mb-2">
            <div className="flex items-center space-x-4">
                <img src="https://apmbb.com.br/resultados/brasao.png" alt="Brasão APMBB" className="h-12 w-12" />
                <div>
                    <h1 className="text-3xl font-bold text-gray-800">Controle de Notas CFO</h1>
                    <p className="text-base text-gray-600">
                        {userProfile?.turma_nome ? `Turma: ${userProfile.turma_nome}` : `Pelotão: ${userProfile?.pelotao_nome || 'N/D'}`}
                        {` - Bem vindo: ${userProfile?.nome}`}
                    </p>
                </div>
            </div>
        </header>
        
        <div className="flex justify-start mb-6"> 
            <div className="flex space-x-1 bg-white p-1 rounded-2xl shadow-sm border border-gray-200">
                <button 
                    onClick={() => setActiveTab('calculo')} 
                    className={`flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 ease-in-out ${
                        activeTab === 'calculo' 
                        ? 'bg-blue-600 text-white shadow-md transform scale-105' 
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                    <CalculatorIcon /> Cálculo de Notas Pessoal
                </button>
                <button 
                    onClick={() => setActiveTab('classificacao')} 
                    className={`flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 ease-in-out ${
                        activeTab === 'classificacao' 
                        ? 'bg-blue-600 text-white shadow-md transform scale-105' 
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                    <ChartBarIcon /> Classificação Geral
                </button>
            </div>
        </div>

        {activeTab === 'calculo' && (
            <div className="calculo-notas-container">
                <div className="medias-gerais-grid">
                    <MediaCard title="MÉDIA INTELECTUAL" value={mediaIntelectualGeral} variant="default" icon={<IntellectualIcon />} />
                    <MediaCard title="MÉDIA CONDUTA" value={mediaConduta} variant="conduta" icon={<ConductIcon />} />
                    <MediaCard title="MÉDIA FINAL" value={mediaFinal} variant="final" icon={<FinalIcon />} />
                </div>

                <section className="notas-section bg-white p-4 rounded-lg shadow-md mt-6">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-semibold">Notas Intelectuais (Matérias)</h3>
                        <div className="flex gap-2">
                            <Button onClick={() => setIsDeleteModalOpen(true)} variant="danger" className="flex items-center"><BlockIcon /> Limpar Tudo</Button>
                        </div>
                    </div>
                    
                    <div className="overflow-x-auto">
                        <table className="notas-table w-full text-sm">
                            <thead>
                                <tr>
                                    <th>Matéria</th>
                                    <th className="text-center">1ª VC</th>
                                    <th className="text-center">2ª VC</th>
                                    <th className="text-center">VE</th>
                                    <th className="text-center">Média</th>
                                    <th className="text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {materiasExibicao.length > 0 ? materiasExibicao.map(m => (
                                    <tr key={m.id} className="hover:bg-gray-50">
                                        <td className="font-medium text-gray-700 py-3">{m.nome}</td>
                                        <td className="whitespace-nowrap text-center">{m.vc1 ?? '-'}</td>
                                        <td className="whitespace-nowrap text-center">{m.vc2 ?? '-'}</td>
                                        <td className="whitespace-nowrap text-center">{m.ve ?? '-'}</td>
                                        <td className="whitespace-nowrap text-center font-bold text-blue-600">{calcularMediaMateria(m).toFixed(2)}</td>
                                        <td className="text-right py-3">
                                            <div className="flex justify-end gap-3">
                                                <button onClick={() => handleOpenModal(m)} className="text-blue-600 hover:text-blue-800" title="Lançar Notas">
                                                    <EditIcon />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="6" className="text-center py-8 text-gray-500">
                                            Nenhuma matéria encontrada para o seu curso.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>

                <section className="notas-section bg-white p-4 rounded-lg shadow-md mt-6"><div className="flex justify-between items-center mb-4"><h3 className="text-xl font-semibold">Nota de Conduta</h3><Button onClick={handleOpenCondutaModal} variant="outline">Editar Notas</Button></div><p className="text-sm text-gray-600">Média atual: <strong className="text-lg">{mediaConduta.toFixed(2)}</strong></p></section>

                <Modal show={isModalOpen} onClose={handleCloseModal} title="Lançar Notas">
                    <form onSubmit={handleModalSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Matéria</label>
                            <input 
                                name="nome" 
                                value={formData.nome} 
                                className="w-full border p-2 rounded bg-gray-100 text-gray-600 cursor-not-allowed" 
                                readOnly 
                            />
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                            <div><label>VC1</label><input type="number" step="0.1" name="vc1" value={formData.vc1 ?? ''} onChange={handleFormChange} className="w-full border p-2 rounded" /></div>
                            <div><label>VC2</label><input type="number" step="0.1" name="vc2" value={formData.vc2 ?? ''} onChange={handleFormChange} className="w-full border p-2 rounded" /></div>
                            <div><label>VE</label><input type="number" step="0.1" name="ve" value={formData.ve ?? ''} onChange={handleFormChange} className="w-full border p-2 rounded" /></div>
                        </div>
                        <div className="flex justify-end gap-3 pt-4 border-t">
                            <Button onClick={handleCloseModal} variant="secondary">Cancelar</Button>
                            <Button type="submit" variant="primary" disabled={isProcessing}>Salvar Notas</Button>
                        </div>
                    </form>
                </Modal>

                <Modal show={isCondutaModalOpen} onClose={() => setIsCondutaModalOpen(false)} title="Editar Notas de Conduta"><form onSubmit={handleCondutaModalSubmit}><div className="conduta-grid">{MESES.map(mes => (<div key={mes} className="conduta-input-group"><label>{mes}</label><input type="number" step="0.1" name={mes} value={condutaFormData[mes] ?? ''} onChange={(e) => setCondutaFormData(prev => ({...prev, [e.target.name]: e.target.value === '' ? null : parseFloat(e.target.value)}))} /></div>))}</div><div className="flex justify-end gap-3 pt-4 border-t mt-4"><Button onClick={() => setIsCondutaModalOpen(false)} variant="secondary">Cancelar</Button><Button type="submit" variant="primary" disabled={isProcessing}>Salvar</Button></div></form></Modal>
                
                <Modal show={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} title="Limpar Todas as Notas">
                    <div>
                        <p>Deseja zerar TODAS as notas lançadas (Matérias e Conduta)?</p>
                        <p className="text-sm text-gray-500 mt-2">As matérias permanecerão na lista, apenas os valores serão removidos.</p>
                        <div className="flex justify-end gap-3 mt-4">
                            <Button onClick={() => setIsDeleteModalOpen(false)} variant="secondary">Cancelar</Button>
                            <Button onClick={handleDeleteAllNotes} variant="danger" disabled={isProcessing}>Limpar Tudo</Button>
                        </div>
                    </div>
                </Modal>
            </div>
        )}

        {activeTab === 'classificacao' && (
            <div className="flex flex-col h-full">
                {turmasList.length > 0 ? (
                    <div className="flex space-x-2 bg-gray-100 p-2 rounded-t-lg overflow-x-auto border-b border-gray-200">
                        {turmasList.map(t => (
                            <button
                                key={t.id}
                                onClick={() => setSelectedTurma(t)}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                                    selectedTurma?.id === t.id
                                    ? 'bg-white text-blue-600 shadow ring-1 ring-black ring-opacity-5'
                                    : 'text-gray-600 hover:bg-gray-200'
                                }`}
                            >
                                {t.nome}
                            </button>
                        ))}
                    </div>
                ) : (
                    <div className="p-2 bg-gray-100 text-sm text-gray-500 text-center">
                        Nenhuma turma "Aspirantes" encontrada.
                    </div>
                )}
                
                <div 
    className="flex-1 bg-white border border-gray-200 rounded-b-lg relative overflow-visible"
    style={{ minHeight: 'calc(100vh - 200px)' }}
>
    {selectedTurma?.nome === 'Aspirantes 2026' ? (
                        <iframe 
                            srcDoc={getClassificacaoHtml(
                                'https://docs.google.com/spreadsheets/d/e/2PACX-1vRpBM9fE28fxi640n4e3kbR4XVdlqkt4Wkc-3JydlK_m6LUnF8idfTli58wGEoi1Q/pub?gid=1099414346&single=true&output=csv',
                                'Classificação - Aspirantes 2026'
                            )} 
                            title="Classificação Aspirantes 2026" 
                            style={{ width: '100%', height: 'auto', minHeight: '100vh', border: 'none' }} 
                        />
                    ) : selectedTurma?.nome === 'Aspirantes 2027' ? (
                        <iframe 
                            srcDoc={getClassificacaoHtml(
                                'https://docs.google.com/spreadsheets/d/e/2PACX-1vTwwq39kmpoy-Brxr_IqsBaiRE1rBy9Yzt1swt3N98sdBzxQiRR0Nlm6rrhY2ZBiw/pub?gid=1099414346&single=true&output=csv',
                                'Classificação - Aspirantes 2027'
                            )} 
                            title="Classificação Aspirantes 2027" 
                            style={{ width: '100%', height: 'auto', minHeight: '100vh', border: 'none' }} 
                        />
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full text-center p-8 bg-gray-50">
                            <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.384-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                            </svg>
                            <h3 className="text-xl font-bold text-gray-600">Em desenvolvimento</h3>
                            <p className="text-sm text-gray-500 mt-2">
                                A classificação para a turma <strong>{selectedTurma?.nome}</strong> estará disponível em breve.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        )}
    </div>
  );
};

export default CalculoNotas;